import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Shield, Users, Swords, Ticket, Plus, Trash2, Ban, 
  CheckCircle, XCircle, Copy, RefreshCw, BarChart3, Key, StopCircle, Flame, Play
} from "lucide-react";
import Navbar from "@/components/Navbar";

const AdminPage = () => {
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [debates, setDebates] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [arenaTakes, setArenaTakes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New invite code form
  const [newCodeRole, setNewCodeRole] = useState("viewer");
  const [newCodeMaxUses, setNewCodeMaxUses] = useState(1);
  const [newCodeExpiresDays, setNewCodeExpiresDays] = useState(30);
  const [creatingCode, setCreatingCode] = useState(false);

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) return;
    
    if (!user) {
      navigate("/login");
      return;
    }
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/browse");
      return;
    }
    fetchAllData();
  }, [user, isAdmin, authLoading, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes, debatesRes, codesRes, arenaTakesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/debates`, { headers }),
        axios.get(`${API}/admin/invite-codes`, { headers }),
        axios.get(`${API}/arena/takes/pending`, { headers }).catch(() => ({ data: { takes: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setDebates(debatesRes.data.debates);
      setInviteCodes(codesRes.data);
      setArenaTakes(arenaTakesRes.data.takes || []);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const createInviteCode = async () => {
    setCreatingCode(true);
    try {
      const res = await axios.post(`${API}/admin/invite-codes`, {
        role: newCodeRole,
        max_uses: newCodeMaxUses,
        expires_in_days: newCodeExpiresDays
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invite code created: ${res.data.code}`);
      setInviteCodes([res.data, ...inviteCodes]);
    } catch (e) {
      toast.error("Failed to create invite code");
    } finally {
      setCreatingCode(false);
    }
  };

  const revokeInviteCode = async (codeId) => {
    try {
      await axios.delete(`${API}/admin/invite-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invite code revoked");
      setInviteCodes(inviteCodes.map(c => c.code_id === codeId ? {...c, is_active: false} : c));
    } catch (e) {
      toast.error("Failed to revoke code");
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User updated");
      setUsers(users.map(u => u.user_id === userId ? {...u, ...updates} : u));
    } catch (e) {
      toast.error("Failed to update user");
    }
  };

  const disableUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User disabled");
      setUsers(users.map(u => u.user_id === userId ? {...u, is_active: false} : u));
    } catch (e) {
      toast.error("Failed to disable user");
    }
  };

  const resetUserPassword = async (userId) => {
    if (!window.confirm("Reset this user's password? A temporary password will be generated.")) return;
    
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show the temporary password in an alert so admin can copy it
      const tempPassword = res.data.temporary_password;
      toast.success(`Password reset! Temporary: ${tempPassword}`, { duration: 10000 });
      
      // Also copy to clipboard
      navigator.clipboard.writeText(tempPassword);
      toast.info("Password copied to clipboard!");
    } catch (e) {
      toast.error("Failed to reset password");
    }
  };

  const updateDebate = async (debateId, updates) => {
    try {
      await axios.put(`${API}/admin/debates/${debateId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Debate updated");
      setDebates(debates.map(d => d.debate_id === debateId ? {...d, ...updates} : d));
    } catch (e) {
      toast.error("Failed to update debate");
    }
  };

  const disableDebate = async (debateId) => {
    try {
      await axios.delete(`${API}/admin/debates/${debateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Debate disabled");
      setDebates(debates.map(d => d.debate_id === debateId ? {...d, is_active: false} : d));
    } catch (e) {
      toast.error("Failed to disable debate");
    }
  };

  const permanentlyDeleteDebate = async (debateId, title) => {
    if (!window.confirm(`⚠️ PERMANENT DELETE\n\nAre you sure you want to permanently delete "${title}"?\n\nThis will:\n- Delete all video recordings from cloud storage\n- Delete all votes and comments\n- This action CANNOT be undone!`)) {
      return;
    }
    
    try {
      const res = await axios.delete(`${API}/admin/debates/${debateId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Debate deleted permanently. ${res.data.videos_deleted} videos removed from cloud.`);
      setDebates(debates.filter(d => d.debate_id !== debateId));
    } catch (e) {
      toast.error("Failed to delete debate permanently");
    }
  };

  const forceEndDebate = async (debateId, title) => {
    if (!window.confirm(`⚠️ FORCE END DEBATE\n\nAre you sure you want to force-end "${title}"?\n\nThis will:\n- Immediately end the debate\n- Determine a winner based on current votes/rounds\n- Update participant stats accordingly`)) {
      return;
    }
    
    try {
      const res = await axios.post(`${API}/admin/debates/${debateId}/force-end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Debate ended: ${res.data.end_reason}`);
      // Refresh debates list
      const debatesRes = await axios.get(`${API}/admin/debates`, { headers: { Authorization: `Bearer ${token}` }});
      setDebates(debatesRes.data.debates || debatesRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to force-end debate");
    }
  };

  const updateArenaTakeStatus = async (takeId, status) => {
    try {
      await axios.put(`${API}/arena/take/${takeId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Take ${status}!`);
      // Remove from pending list
      setArenaTakes(arenaTakes.filter(t => t.take_id !== takeId));
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${status} take`);
    }
  };

  const deleteArenaTake = async (takeId) => {
    if (!window.confirm("Delete this take? This will also remove the video from storage.")) return;
    try {
      await axios.delete(`${API}/arena/take/${takeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Take deleted!");
      setArenaTakes(arenaTakes.filter(t => t.take_id !== takeId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete take");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] relative">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative">
      {/* Ambient red/blue glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[20%] w-[70%] h-[50%] rounded-full bg-red-600/20 blur-[100px]" />
        <div className="absolute -bottom-[20%] -right-[20%] w-[70%] h-[50%] rounded-full bg-blue-600/25 blur-[100px]" />
      </div>
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center">
            <Shield className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage users, debates, and invite codes</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-auto"
            onClick={fetchAllData}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card/30 border border-white/10 rounded-lg p-4">
              <Users className="w-5 h-5 text-muted-foreground mb-2" />
              <p className="text-2xl font-bold mono">{stats.users.total}</p>
              <p className="text-xs text-muted-foreground">Total Users</p>
            </div>
            <div className="bg-card/30 border border-white/10 rounded-lg p-4">
              <Swords className="w-5 h-5 text-muted-foreground mb-2" />
              <p className="text-2xl font-bold mono">{stats.debates.total}</p>
              <p className="text-xs text-muted-foreground">Total Debates</p>
            </div>
            <div className="bg-card/30 border border-white/10 rounded-lg p-4">
              <BarChart3 className="w-5 h-5 text-muted-foreground mb-2" />
              <p className="text-2xl font-bold mono">{stats.votes.total}</p>
              <p className="text-xs text-muted-foreground">Total Votes</p>
            </div>
            <div className="bg-card/30 border border-white/10 rounded-lg p-4">
              <Ticket className="w-5 h-5 text-muted-foreground mb-2" />
              <p className="text-2xl font-bold mono">{stats.invite_codes.active}</p>
              <p className="text-xs text-muted-foreground">Active Codes</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="invites" className="w-full">
          <TabsList className="bg-card/30 border border-white/10 mb-8 flex-wrap">
            <TabsTrigger value="invites" data-testid="admin-tab-invites">
              <Ticket className="w-4 h-4 mr-2" />
              Invite Codes
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">
              <Users className="w-4 h-4 mr-2" />
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="debates" data-testid="admin-tab-debates">
              <Swords className="w-4 h-4 mr-2" />
              Debates ({debates.length})
            </TabsTrigger>
            <TabsTrigger value="arena" data-testid="admin-tab-arena">
              <Flame className="w-4 h-4 mr-2" />
              Arena Takes {arenaTakes.length > 0 && <Badge variant="destructive" className="ml-2 text-xs">{arenaTakes.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Invite Codes Tab */}
          <TabsContent value="invites">
            <div className="bg-card/30 border border-white/10 rounded-lg p-6 mb-6">
              <h3 className="font-bold mb-4">Generate New Invite Code</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Role</Label>
                  <Select value={newCodeRole} onValueChange={setNewCodeRole}>
                    <SelectTrigger data-testid="new-code-role" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">Viewer (watch & vote)</SelectItem>
                      <SelectItem value="debater">Debater (can participate)</SelectItem>
                      <SelectItem value="admin">Admin (full access)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max Uses</Label>
                  <Input
                    type="number"
                    data-testid="new-code-max-uses"
                    value={newCodeMaxUses}
                    onChange={(e) => setNewCodeMaxUses(parseInt(e.target.value) || 1)}
                    min={1}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Expires In (days)</Label>
                  <Input
                    type="number"
                    data-testid="new-code-expires"
                    value={newCodeExpiresDays}
                    onChange={(e) => setNewCodeExpiresDays(parseInt(e.target.value) || 30)}
                    min={1}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    data-testid="create-invite-code"
                    onClick={createInviteCode}
                    disabled={creatingCode}
                    className="w-full bg-primary text-primary-foreground"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Generate
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-card/30 border border-white/10 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inviteCodes.map((code) => (
                    <TableRow key={code.code_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono">{code.code}</code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(code.code)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={code.role === "admin" ? "destructive" : code.role === "debater" ? "default" : "secondary"}>
                          {code.role}
                        </Badge>
                      </TableCell>
                      <TableCell>{code.current_uses} / {code.max_uses}</TableCell>
                      <TableCell>
                        {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : "Never"}
                      </TableCell>
                      <TableCell>
                        {code.is_active ? (
                          <Badge variant="outline" className="text-green-500 border-green-500">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-500 border-red-500">Revoked</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {code.is_active && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => revokeInviteCode(code.code_id)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-card/30 border border-white/10 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Stats</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id}>
                      <TableCell className="font-medium">{u.username}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Select 
                          value={u.role} 
                          onValueChange={(value) => updateUser(u.user_id, { role: value })}
                          disabled={u.user_id === user.user_id}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="debater">Debater</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {u.wins}W / {u.losses}L / {u.total_debates} debates
                        </span>
                      </TableCell>
                      <TableCell>
                        {u.is_active ? (
                          <Badge variant="outline" className="text-green-500 border-green-500">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-500 border-red-500">Disabled</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {/* Reset Password Button */}
                          {u.user_id !== user.user_id && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => resetUserPassword(u.user_id)}
                              className="text-yellow-500 hover:text-yellow-600"
                              title="Reset Password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Disable/Enable Button */}
                          {u.user_id !== user.user_id && u.is_active && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => disableUser(u.user_id)}
                              className="text-red-500 hover:text-red-600"
                              title="Disable User"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          {u.user_id !== user.user_id && !u.is_active && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => updateUser(u.user_id, { is_active: true })}
                              className="text-green-500 hover:text-green-600"
                              title="Enable User"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Debates Tab */}
          <TabsContent value="debates">
            {/* AI Topic Generator */}
            <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
              <h3 className="font-bold flex items-center gap-2 mb-3">
                🤖 AI Topic Generator
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                <div className="md:col-span-2">
                  <Label className="text-xs text-muted-foreground mb-1 block">Select Category</Label>
                  <select 
                    id="ai-category-select"
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">🎲 Random Mix (All Categories)</option>
                    <option value="sports">🏀 Sports</option>
                    <option value="hip-hop">🎤 Hip Hop</option>
                    <option value="politics">🏛️ Politics</option>
                    <option value="technology">💻 Technology</option>
                    <option value="entertainment">🎬 Entertainment</option>
                    <option value="gaming">🎮 Gaming</option>
                    <option value="lifestyle">💪 Lifestyle</option>
                    <option value="culture">🎨 Culture</option>
                    <option value="food">🍔 Food</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Choose a category or leave as &quot;Random Mix&quot; for variety
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">How Many?</Label>
                  <select 
                    id="ai-topic-count"
                    className="w-full bg-zinc-900/50 border border-white/10 rounded-md px-3 py-2 text-sm"
                    defaultValue="3"
                  >
                    <option value="1">1 Topic</option>
                    <option value="3">3 Topics</option>
                    <option value="5">5 Topics</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={async () => {
                      const btn = document.getElementById('ai-generate-btn');
                      const category = document.getElementById('ai-category-select').value;
                      const count = parseInt(document.getElementById('ai-topic-count').value) || 3;
                      btn.disabled = true;
                      btn.innerText = 'Generating...';
                      try {
                        const payload = category 
                          ? { category, count }
                          : { count };
                        const res = await axios.post(`${API}/admin/debates/generate-topics`, payload, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(`🎉 Created ${res.data.debates?.length || 0} AI-generated debate${res.data.debates?.length === 1 ? '' : 's'}!`);
                        // Refresh debates list
                        const debatesRes = await axios.get(`${API}/admin/debates`, { headers: { Authorization: `Bearer ${token}` }});
                        setDebates(debatesRes.data.debates);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed to generate topics");
                      } finally {
                        btn.disabled = false;
                        btn.innerText = '🤖 Generate';
                      }
                    }}
                    id="ai-generate-btn"
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 h-10"
                  >
                    🤖 Generate
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Test Debate Button */}
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-yellow-400 flex items-center gap-2">
                    🧪 Solo Test Mode
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create a test debate where YOU control both Red and Blue sides. Perfect for testing recording & playback.
                  </p>
                </div>
                <Button 
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/admin/debates/create-test`, {
                        title: "Admin Test Debate",
                        description: "Testing the full debate flow",
                        category: "technology",
                        debate_type: "video",
                        max_round_time: 60  // 1 minute for test debates
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success("🧪 Test debate created! Opening...");
                      // Navigate to the debate
                      window.location.href = `/debate/${res.data.debate_id}`;
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to create test debate");
                    }
                  }}
                  className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
                >
                  🧪 Create Test Debate
                </Button>
              </div>
            </div>

            {/* Create Arena Section */}
            <div className="bg-card/30 border border-white/10 rounded-lg p-4 mb-4">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Create Open Arena (Both Spots Open)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input 
                  placeholder="Debate Title" 
                  id="arena-title"
                  className="bg-zinc-900/50 border-white/10"
                />
                <Input 
                  placeholder="Description" 
                  id="arena-description"
                  className="bg-zinc-900/50 border-white/10"
                />
                <select 
                  id="arena-category"
                  className="bg-zinc-900/50 border border-white/10 rounded-md px-3 py-2 text-sm"
                >
                  <option value="sports">Sports</option>
                  <option value="hip-hop">Hip Hop</option>
                  <option value="politics">Politics</option>
                  <option value="technology">Technology</option>
                  <option value="entertainment">Entertainment</option>
                  <option value="gaming">Gaming</option>
                  <option value="lifestyle">Lifestyle</option>
                  <option value="culture">Culture</option>
                  <option value="food">Food</option>
                </select>
                <Button 
                  onClick={async () => {
                    const title = document.getElementById('arena-title').value;
                    const description = document.getElementById('arena-description').value;
                    const category = document.getElementById('arena-category').value;
                    if (!title) { toast.error("Enter a title"); return; }
                    try {
                      const response = await axios.post(`${API}/admin/debates/arena`, 
                        { title, description: description || title, category, debate_type: "video" },
                        { headers: { Authorization: `Bearer ${token}` }}
                      );
                      toast.success("Arena created! Both spots open for testers.");
                      document.getElementById('arena-title').value = '';
                      document.getElementById('arena-description').value = '';
                      // Navigate to the created debate
                      if (response.data?.debate_id) {
                        navigate(`/debate/${response.data.debate_id}`);
                      } else {
                        // Refresh debates if no debate_id returned
                        const res = await axios.get(`${API}/admin/debates`, { headers: { Authorization: `Bearer ${token}` }});
                        setDebates(res.data);
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to create arena");
                    }
                  }}
                >
                  Create Arena
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Creates a debate where you are NOT a participant. Testers can pick Red or Blue corner.
              </p>
            </div>

            <div className="bg-card/30 border border-white/10 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Participants</TableHead>
                    <TableHead>Votes</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {debates.map((d) => (
                    <TableRow key={d.debate_id}>
                      <TableCell className="font-medium max-w-48 truncate">
                        {d.title}
                        {d.is_arena && <Badge variant="outline" className="ml-2 text-xs">Arena</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{d.category}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={d.status === "live" ? "destructive" : d.status === "open" ? "secondary" : "outline"}>
                          {d.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {d.red_corner_username || "Open"} vs {d.blue_corner_username || "Open"}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          <span className="text-red-500">{d.red_votes}</span> - <span className="text-blue-500">{d.blue_votes}</span>
                        </span>
                      </TableCell>
                      <TableCell>
                        {d.is_active ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {/* Force End - only for live/open debates */}
                          {(d.status === "live" || d.status === "open") && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => forceEndDebate(d.debate_id, d.title)}
                              className="text-orange-500 hover:text-orange-600 hover:bg-orange-100"
                              title="Force end debate (determines winner based on current state)"
                            >
                              <StopCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {d.is_active ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => disableDebate(d.debate_id)}
                              className="text-red-500 hover:text-red-600"
                              title="Disable debate"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => updateDebate(d.debate_id, { is_active: true })}
                              className="text-green-500 hover:text-green-600"
                              title="Enable debate"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => permanentlyDeleteDebate(d.debate_id, d.title)}
                            className="text-red-700 hover:text-red-800 hover:bg-red-100"
                            title="Permanently delete (removes videos from cloud)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Arena Takes Tab */}
          <TabsContent value="arena">
            <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-bold flex items-center gap-2 text-orange-400">
                    <Flame className="w-5 h-5" />
                    Arena Content Moderation
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Review and approve/reject user-submitted Arena takes before they go live
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/arena/takes/pending`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setArenaTakes(res.data.takes || []);
                      toast.success("Refreshed pending takes");
                    } catch (e) {
                      toast.error("Failed to refresh");
                    }
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {arenaTakes.length === 0 ? (
              <div className="bg-card/30 border border-white/10 rounded-lg p-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">All Caught Up!</h3>
                <p className="text-muted-foreground">No pending Arena takes to review</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {arenaTakes.map((take) => (
                  <div key={take.take_id} className="bg-card/30 border border-white/10 rounded-lg overflow-hidden">
                    {/* Video Preview */}
                    <div className="relative aspect-video bg-black">
                      <video
                        src={take.video_url}
                        className="w-full h-full object-contain"
                        controls
                        playsInline
                        preload="metadata"
                      />
                      <Badge 
                        className={`absolute top-2 left-2 ${take.side === 'a' ? 'bg-red-600' : 'bg-blue-600'}`}
                      >
                        Side {take.side?.toUpperCase()}
                      </Badge>
                    </div>
                    
                    {/* Take Info */}
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-sm font-bold">
                          {take.username?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium">{take.username || 'Unknown User'}</p>
                          <p className="text-xs text-muted-foreground">
                            {take.created_at ? new Date(take.created_at).toLocaleString() : 'Unknown time'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-xs text-muted-foreground mb-3">
                        <p>Matchup: {take.matchup_id?.substring(0, 12)}...</p>
                        <p>Take ID: {take.take_id?.substring(0, 12)}...</p>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 mb-2">
                        <Button 
                          onClick={() => updateArenaTakeStatus(take.take_id, 'approved')}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button 
                          onClick={() => updateArenaTakeStatus(take.take_id, 'rejected')}
                          variant="destructive"
                          className="flex-1"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                      {/* Delete button for testing */}
                      <Button 
                        onClick={() => deleteArenaTake(take.take_id)}
                        variant="ghost"
                        className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        size="sm"
                      >
                        <Trash2 className="w-3 h-3 mr-2" />
                        Delete (allows re-record)
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
